import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cn } from "@/lib/utils"

const iconButtonBaseClassName =
    "inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"

const iconButtonVariantClasses = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    destructive:
        "bg-destructive text-destructive-foreground hover:bg-destructive/90",
    outline:
        "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
    secondary:
        "bg-secondary text-secondary-foreground hover:bg-secondary/80",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    link: "text-primary underline-offset-4 hover:underline",
} as const

const iconButtonSizeClasses = {
    default: "h-10 w-10",
    sm: "h-9 w-9",
    lg: "h-11 w-11",
    icon: "h-10 w-10",
} as const

type IconButtonVariant = keyof typeof iconButtonVariantClasses
type IconButtonSize = keyof typeof iconButtonSizeClasses

const iconButtonVariants = ({
    variant = "default",
    size = "default",
    className,
}: {
    variant?: IconButtonVariant | null
    size?: IconButtonSize | null
    className?: string
} = {}) =>
    cn(
        iconButtonBaseClassName,
        iconButtonVariantClasses[variant ?? "default"],
        iconButtonSizeClasses[size ?? "default"],
        className
    )

export interface IconButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    asChild?: boolean
    variant?: IconButtonVariant
    size?: IconButtonSize
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : "button"
        return (
            <Comp
                className={cn(iconButtonVariants({ variant, size, className }))}
                ref={ref}
                {...props}
            />
        )
    }
)
IconButton.displayName = "IconButton"

export { IconButton, iconButtonVariants }
